// src/pages/purchase-orders/components/SupplierInvoicesList.tsx
import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast, { Toaster } from 'react-hot-toast';

interface FactureFournisseur {
  id: number;
  numero_facture_fournisseur: string;
  fournisseur: {
    id: number;
    raison_sociale: string;
  };
  date_facture: string;
  date_echeance: string | null;
  total_ttc: number;
  montant_paye: number;
  statut_paiement: 'IMPAYEE' | 'PAYEE_PARTIEL' | 'PAYEE' | 'ANNULEE';
  notes?: string;
  brs?: any[];
  paiements?: any[];
}

interface Stats {
  total: number;
  impayees: number;
  partielles: number;
  payees: number;
  montantDu: number;
  alertes: number;
}

export default function SupplierInvoicesList({
  onViewDetails,
  onAddPayment,
  onAddInvoice,
}: {
  onViewDetails: (invoice: FactureFournisseur) => void;
  onAddPayment: (invoice: FactureFournisseur) => void;
  onAddInvoice: () => void;
}) {
  const [invoices, setInvoices] = useState<FactureFournisseur[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await getResources('factures-fournisseur', {
        params: { with: 'fournisseur,bonsReception,paiements' }
      });
      setInvoices(res.data.data || res.data || []);
    } catch (err) {
      toast.error('Erreur de chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(amount) + ' GNF';
  };

  const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const getStatutDisplay = (statut: string) => {
    switch (statut) {
      case 'PAYEE': return { label: 'Soldée', color: 'bg-green-100 text-green-800', icon: 'ri-checkbox-circle-fill' };
      case 'PAYEE_PARTIEL': return { label: 'Payée Partiellement', color: 'bg-orange-100 text-orange-800', icon: 'ri-pie-chart-line' };
      case 'IMPAYEE': return { label: 'Impayée', color: 'bg-yellow-100 text-yellow-800', icon: 'ri-time-line' };
      default: return { label: statut, color: 'bg-gray-100 text-gray-800', icon: 'ri-file-line' };
    }
  };

  const joursRestants = (dateEcheance: string | null) => {
    if (!dateEcheance) return null;
    const diff = new Date(dateEcheance).getTime() - new Date().setHours(0, 0, 0, 0);
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getUrgence = (jours: number | null) => {
    if (jours === null) return null;
    if (jours < 0) return { label: `Retard ${Math.abs(jours)}j`, color: 'text-red-600' };
    if (jours <= 3) return { label: `${jours}j`, color: 'text-orange-600' };
    if (jours <= 7) return { label: `${jours}j`, color: 'text-yellow-600' };
    return null;
  };

  const filteredInvoices = invoices.filter(inv => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = inv.numero_facture_fournisseur.toLowerCase().includes(search) ||
                         inv.fournisseur.raison_sociale.toLowerCase().includes(search);
    const matchesStatus = statusFilter === 'all' || inv.statut_paiement === statusFilter;
    const matchesSupplier = supplierFilter === 'all' || inv.fournisseur.raison_sociale === supplierFilter;
    return matchesSearch && matchesStatus && matchesSupplier;
  });

  const suppliers = [...new Set(invoices.map(i => i.fournisseur.raison_sociale))];

  const stats: Stats = {
    total: invoices.length,
    impayees: invoices.filter(i => i.statut_paiement === 'IMPAYEE').length,
    partielles: invoices.filter(i => i.statut_paiement === 'PAYEE_PARTIEL').length,
    payees: invoices.filter(i => i.statut_paiement === 'PAYEE').length,
    montantDu: invoices.reduce((sum, i) => sum + (i.total_ttc - i.montant_paye), 0),
    alertes: invoices.filter(i => {
      const jours = joursRestants(i.date_echeance);
      return jours !== null && jours <= 7;
    }).length
  };

  if (loading) {
    return <div className="text-center py-12">Chargement des factures...</div>;
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Factures</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-3-line text-xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Impayées</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.impayees}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-xl text-yellow-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Partielles</p>
              <p className="text-2xl font-bold text-orange-600">{stats.partielles}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-pie-chart-line text-xl text-orange-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Soldées</p>
              <p className="text-2xl font-bold text-green-600">{stats.payees}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-xl text-green-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">À Payer</p>
              <p className="text-lg font-bold text-red-600">{formatAmount(stats.montantDu)}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-euro-circle-line text-xl text-red-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Alertes</p>
              <p className="text-2xl font-bold text-red-600">{stats.alertes}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-alarm-warning-fill text-xl text-red-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Rechercher par numéro ou fournisseur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="all">Tous les statuts</option>
              <option value="IMPAYEE">Impayées</option>
              <option value="PAYEE_PARTIEL">Payées partiellement</option>
              <option value="PAYEE">Soldées</option>
            </select>

            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="all">Tous les fournisseurs</option>
              {suppliers.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <button
              onClick={onAddInvoice}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"
            >
              <i className="ri-add-line"></i>
              <span>Nouvelle Facture</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facture</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Échéance</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant TTC</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Restant</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => {
                const statut = getStatutDisplay(invoice.statut_paiement);
                const jours = joursRestants(invoice.date_echeance);
                const urgence = getUrgence(jours);

                return (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                      {invoice.numero_facture_fournisseur}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {invoice.fournisseur.raison_sociale}
                    </td>
                    <td className="px-4 py-4 text-center text-sm text-gray-900">
                      {formatDate(invoice.date_facture)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="text-sm text-gray-900">{formatDate(invoice.date_echeance)}</div>
                      {urgence && (
                        <div className={`text-xs font-bold ${urgence.color} mt-1`}>{urgence.label}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right text-sm font-medium">
                      {formatAmount(invoice.total_ttc)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="text-sm font-bold text-red-600">
                        {formatAmount(invoice.total_ttc - invoice.montant_paye)}
                      </div>
                      {invoice.montant_paye > 0 && (
                        <div className="text-xs text-gray-500">
                          Payé: {formatAmount(invoice.montant_paye)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statut.color}`}>
                        <i className={statut.icon + " mr-1"}></i>
                        {statut.label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => onViewDetails(invoice)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Détails"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                        {invoice.statut_paiement !== 'PAYEE' && (
                          <button
                            onClick={() => onAddPayment(invoice)}
                            className="text-green-600 hover:text-green-800"
                            title="Régler"
                          >
                            <i className="ri-money-euro-circle-line text-lg"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredInvoices.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500">
            <i className="ri-file-list-3-line text-4xl mb-4"></i>
            <p>Aucune facture trouvée</p>
          </div>
        )}
      </div>
    </div>
  );
}